<!--
  - SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->
<template>
	<div class="options">
		<div v-if="showOptions && (config.canReadRows || (config.canCreateRows && rows.length > 0))" class="fix-col-4">
			<div :class="{ 'add-padding-left': isSmallMobile }" class="actionButtonsLeft">
				<NcButton v-if="!isSmallMobile && config.canCreateRows" :aria-label="t('tables', 'Create row')"
					:close-after-click="true" type="tertiary" data-cy="createRowBtn" @click="$emit('create-row')">
					{{ t('tables', 'Create row') }}
					<template #icon>
						<Plus :size="25" />
					</template>
				</NcButton>
				<NcButton v-if="isSmallMobile && config.canCreateRows" :close-after-click="true"
					:aria-label="t('tables', 'Create Row')" type="tertiary" data-cy="createRowBtn"
					@click="$emit('create-row')">
					<template #icon>
						<Plus :size="25" />
					</template>
				</NcButton>
				<div class="searchAndFilter">
					<SearchForm :columns="columns" :search-string="getSearchString"
						@set-search-string="str => $emit('set-search-string', str)" />
				</div>
				<PaginationBlock :rows="rows" />
			</div>

			<div v-if="selectedRows.length > 0" class="selected-rows-option">
				<div style="padding: 10px; color: var(--color-text-maxcontrast);">
					{{ n('tables', '%n selected row', '%n selected rows', selectedRows.length, {}) }}
				</div>
				<NcActions type="secondary" :force-name="true" :inline="showFullOptions ? 2 : 0">
					<NcActionButton close-after-click data-cy="exportSelectedBtn" @click="exportSelected">
						<template #icon>
							<TrayArrowDown :size="20" />
						</template>
						{{ t('tables', 'Export selected rows') }}
					</NcActionButton>
					<NcActionButton v-if="isFiltered" close-after-click data-cy="exportFilteredBtn" @click="exportFiltered">
						<template #icon>
							<TrayArrowDown :size="20" />
						</template>
						{{ t('tables', 'Export filtered rows') }}
					</NcActionButton>
					<NcActionButton v-if="config.canDeleteRows" close-after-click @click="deleteSelectedRows">
						<template #icon>
							<Delete :size="20" />
						</template>
						{{ t('tables', 'Delete') }}
					</NcActionButton>
					<NcActionButton v-if="!showFullOptions" close-after-click @click="deselectAllRows">
						<template #icon>
							<Check :size="20" />
						</template>
						{{ t('tables', 'Uncheck all') }}
					</NcActionButton>
				</NcActions>
			</div>
			<div v-else class="selected-rows-placeholder" />
		</div>
	</div>
</template>

<script>
import { NcButton, NcActions, NcActionButton } from '@nextcloud/vue'
import { emit } from '@nextcloud/event-bus'
import Plus from 'vue-material-design-icons/Plus.vue'
import Check from 'vue-material-design-icons/CheckboxBlankOutline.vue'
import Delete from 'vue-material-design-icons/TrashCanOutline.vue'
import TrayArrowDown from 'vue-material-design-icons/TrayArrowDown.vue'
import viewportHelper from '../../../mixins/viewportHelper.js'
import SearchForm from '../partials/SearchForm.vue'
import PaginationBlock from './PaginationBlock.vue'
import { translate as t, translatePlural as n } from '@nextcloud/l10n'

export default {
	name: 'Options',

	components: {
		NcActions,
		NcActionButton,
		SearchForm,
		NcButton,
		Plus,
		Check,
		Delete,
		TrayArrowDown,
		PaginationBlock,
	},

	mixins: [viewportHelper],

	props: {
		selectedRows: {
			type: Array,
			default: () => [],
		},
		rows: {
			type: Array,
			default: () => [],
		},
		allRows: {
			type: Array,
			default: () => [],
		},
		elementId: {
			type: Number,
			default: null,
		},
		isView: {
			type: Boolean,
			default: true,
		},
		showOptions: {
			type: Boolean,
			default: true,
		},
		columns: {
			type: Array,
			default: null,
		},
		viewSetting: {
			type: Object,
			default: null,
		},
		config: {
			type: Object,
			default: null,
		},
	},

	emits: [
		'create-row',
		'delete-selected-rows',
		'download-filtered-csv',
		'set-search-string',
	],
	data() {
		return {
			optionsDivWidth: null,
		}
	},

	computed: {
		getSelectedRows() {
			const rows = []
			this.selectedRows.forEach(id => {
				rows.push(this.getRowById(id))
			})
			return rows
		},
		getSearchString() {
			return this.viewSetting?.searchString || ''
		},
		showFullOptions() {
			return this.optionsDivWidth > 800
		},
		isFiltered() {
			return (this.viewSetting?.filter?.length > 0) || !!this.viewSetting?.searchString
		},
	},

	mounted() {
		this.updateOptionsDivWidth()
		window.addEventListener('resize', this.updateOptionsDivWidth)
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.updateOptionsDivWidth)
	},

	methods: {
		t,
		n,
		updateOptionsDivWidth() {
			this.optionsDivWidth = this.$el?.offsetWidth
		},
		exportFiltered() {
			this.$emit('download-filtered-csv', this.rows)
		},
		exportSelected() {
			this.$emit('download-filtered-csv', this.getSelectedRows)
		},
		getRowById(rowId) {
			const index = this.allRows.findIndex(row => row.id === rowId)
			return this.allRows[index]
		},
		deleteSelectedRows() {
			this.$emit('delete-selected-rows', this.selectedRows)
		},
		deselectAllRows() {
			emit('tables:selected-rows:deselect', { elementId: this.elementId, isView: this.isView })
		},
	},
}
</script>

<style scoped lang="scss">
.sticky {
	position: -webkit-sticky;
	/* Safari */
	position: sticky;
	top: 90px;
	inset-inline-start: 0;
}

.fix-col-4 {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
}

.selected-rows-placeholder {
	min-width: fit-content;
	flex-shrink: 0;
}

.selected-rows-option {
	justify-content: flex-end;
	display: inline-flex;
	white-space: nowrap;
	overflow: hidden;
	min-width: fit-content;
}

.add-padding-left {
	padding-inline-start: calc(var(--default-grid-baseline) * 1);
}

:deep(.counter-bubble__counter) {
	max-width: fit-content;
}

.actionButtonsLeft {
	display: inline-flex;
	align-items: center;
	padding-inline-start: calc(var(--default-grid-baseline) * 1);
	flex-wrap: wrap;
	gap: 4px;
}

:deep(.actionButtonsLeft button) {
	min-width: fit-content;
}

.searchAndFilter {
	margin-inline-start: calc(var(--default-grid-baseline) * 3);
	width: auto;
	min-width: 100px;
	flex-shrink: 1;
}

@media only screen and (max-width: 641px) {
	.fix-col-4 {
		flex-direction: column;
		align-items: stretch;
	}

	.actionButtonsLeft {
		justify-content: center;
	}

	.selected-rows-option {
		justify-content: center;
		margin-top: 8px;
	}
}

@media only screen and (max-width: 480px) {
	.searchAndFilter {
		margin-inline-start: calc(var(--default-grid-baseline) * 1);
		min-width: 80px;
		order: -1;
		flex: 1 1 auto;
	}
}

</style>
